/**
 * Pure race-placement logic, kept free of Three.js/DOM so it can be unit-tested.
 *
 * A racer's progress around the loop is a single monotonically increasing
 * number: completed laps + last checkpoint index + a sub-checkpoint fraction
 * derived from the distance to the next checkpoint. Finished racers always rank
 * ahead of still-racing ones; when a finished racer's authoritative place is
 * known it is used directly.
 */
export interface RacerProgress {
  /** Completed laps (0-based until the first finish-line crossing). */
  lap: number;
  /** Index of the last checkpoint passed. */
  cp: number;
  x: number;
  z: number;
  finished: boolean;
  /** Authoritative finishing place (1 = winner) if known, else null. */
  finishPlace: number | null;
}

export interface CP {
  x: number;
  z: number;
}

/** Finished racers score above any in-progress racer. */
const FINISHED_BASE = 1e7;

/**
 * Monotonic progress metric. Within a checkpoint segment the value grows as the
 * racer nears the next checkpoint (1/(1+d) ∈ (0,1]); crossing a checkpoint bumps
 * the integer part, so it never decreases along the intended route.
 */
export function progressValue(lap: number, cp: number, x: number, z: number, checkpoints: CP[]): number {
  const n = checkpoints.length;
  if (n === 0) return lap;
  const next = (cp + 1) % n;
  const np = checkpoints[next];
  const d = Math.hypot(x - np.x, z - np.z);
  return lap * n + cp + 1 / (1 + d);
}

/** Rank score for one racer; higher = better placed. */
export function rankScore(r: RacerProgress, checkpoints: CP[]): number {
  if (r.finished) {
    // Earlier finishers rank higher; with an authoritative place, place 1 wins.
    if (r.finishPlace != null) return FINISHED_BASE * 2 - r.finishPlace;
    return FINISHED_BASE;
  }
  return progressValue(r.lap, r.cp, r.x, r.z, checkpoints);
}

/** 1-based place of `me` among itself and `others`. */
export function computePlace(me: RacerProgress, others: RacerProgress[], checkpoints: CP[]): number {
  if (me.finished && me.finishPlace != null) return me.finishPlace;
  const mine = rankScore(me, checkpoints);
  let ahead = 0;
  for (const o of others) {
    if (rankScore(o, checkpoints) > mine) ahead++;
  }
  return ahead + 1;
}
